import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';

import { Button } from '../../shared/ui/button';
import { FactList, FactRow } from '../../shared/ui/fact-list';
import { Modal } from '../../shared/ui/modal';
import { Note } from '../../shared/ui/note';
import { ErrorState } from '../../shared/ui/states';
import {
  duration,
  measuredPhases,
  phaseLabel,
  probeDestination,
  type TDestination,
  type TProbe,
} from '../../entities/destination';

const title = (probe: TProbe, name: string): string => {
  const status = probe.status === null ? '' : ` ${probe.status}`;
  switch (probe.answer) {
    case 'accepted':
      return `${name} answered${status}`;
    case 'no_head':
      return `${name} is reachable`;
    case 'rejected':
      return `${name} refused the check with${status}`;
    case 'server_error':
      return `${name} answered${status}`;
    default:
      return 'No answer';
  }
};

const NO_ANSWER: Record<string, string> = {
  conn_refused: 'The connection was refused before anything was sent.',
  dns: 'The name did not resolve, so nothing was sent.',
  tls: 'The handshake did not finish, so nothing was sent.',
  timeout: 'It was cut off at the timeout with nothing read back.',
  reset: 'The connection was reset before a reply arrived.',
};

// The probe is a HEAD with no body on purpose: a synthetic POST could create an order.
const meaning = (probe: TProbe, method: string): string => {
  switch (probe.answer) {
    case 'accepted':
      return 'It is up and answering.';
    case 'no_head':
      return `It answered ${probe.status}, which only says it does not take HEAD. Real deliveries are ${method}s.`;
    case 'rejected':
      return `It is up, but refused a HEAD with no body. A real ${method} may be treated differently — a delivery will tell.`;
    case 'server_error':
      return 'It is up, but its server is failing.';
    default:
      return NO_ANSWER[probe.errorClass ?? ''] ?? `The check ended at ${phaseLabel(probe.phase)}.`;
  }
};

const Result = ({ probe, destination }: { probe: TProbe; destination: TDestination }) => {
  const phases = measuredPhases(probe);
  const capped = probe.probeTimeoutMs < probe.configuredTimeoutMs;

  return (
    <>
      <FactList>
        {probe.method && (
          <FactRow label="We sent" mono>
            {`${probe.method}, no body${probe.authSent ? ', with the credential' : ''}`}
          </FactRow>
        )}
        {probe.status !== null ? (
          <FactRow
            label="Answered"
            tone={probe.answer === 'accepted' || probe.answer === 'no_head' ? 'success' : 'danger'}
            mono
          >
            {probe.responseMs === null
              ? probe.status
              : `${probe.status} in ${duration(probe.responseMs)}`}
          </FactRow>
        ) : (
          <FactRow label="Failed at" tone="danger" mono>
            {phaseLabel(probe.phase)}
          </FactRow>
        )}

        <FactRow label="After" mono>
          {duration(probe.totalMs)}
        </FactRow>

        {phases.length > 0 && (
          <FactRow label={phases.map((phase) => phase.name).join(' · ')} mono>
            {`${phases.map((phase) => phase.ms).join(' · ')} ms`}
          </FactRow>
        )}

        {probe.connectionReused === true && (
          <FactRow label="Connection" tone="muted">
            reused — there was no handshake to measure
          </FactRow>
        )}

        {probe.resolvedAddress !== null && (
          <FactRow label="Resolved to" mono>
            {probe.resolvedAddress}
          </FactRow>
        )}

        <FactRow label="Server says">{probe.detail}</FactRow>

        {capped && (
          <FactRow label="Waited" tone="muted">
            {`up to ${duration(probe.probeTimeoutMs)} — deliveries wait ${duration(probe.configuredTimeoutMs)}`}
          </FactRow>
        )}
      </FactList>

      {probe.errorClass === 'timeout' && (
        <Note className="mt-3">
          A delivery cut off at the timeout ends as <span className="font-mono">unknown</span>, not
          as a failure — it may well have arrived.
        </Note>
      )}

      {probe.ok && destination.config.signOutbound && (
        <Note className="mt-3">
          Reachable is all this proves. Whether {destination.name} accepts our signature only a real
          delivery tells.
        </Note>
      )}
    </>
  );
};

interface IProbeProps {
  destination: TDestination;
}

export const DestinationProbe = ({ destination }: IProbeProps) => {
  const [open, setOpen] = useState(false);

  const check = useMutation({
    mutationFn: () => probeDestination(destination.id),
    onSettled: () => setOpen(true),
  });

  const probe = check.data ?? null;
  const method = destination.config.method ?? 'POST';
  // One probe is not evidence; the delivery record is. Only then is pausing worth suggesting.
  const failing = destination.state === 'failing';

  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        waiting={check.isPending}
        waitingLabel="Checking"
        onClick={() => check.mutate()}
      >
        Check it can be reached
      </Button>

      <Modal
        open={open}
        onOpenChange={setOpen}
        width="md"
        title={probe ? title(probe, destination.name) : 'The check did not run'}
        description={probe ? meaning(probe, method) : undefined}
        footer={
          <>
            <Button
              size="sm"
              variant="ghost"
              waiting={check.isPending}
              waitingLabel="Checking"
              onClick={() => check.mutate()}
            >
              Try again
            </Button>
          </>
        }
      >
        {probe ? (
          <Result probe={probe} destination={destination} />
        ) : (
          <ErrorState error={check.error} />
        )}
        {probe && !probe.ok && failing && (
          <Note className="mt-3">
            Deliveries here are failing too. Pausing the flows listed on this page stops retries
            from being spent while it is down.
          </Note>
        )}
      </Modal>
    </>
  );
};
